import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  basculerInterruption,
  choisirDebut,
  choisirFin,
  creerNuitManuelle,
  lireReglages,
  majNuit,
  supprimerInterruption,
  supprimerNuit,
} from '../../db/repo'
import { db } from '../../db/db'
import {
  dayKeyOf,
  formatJour,
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
  todayKey,
} from '../../domain/time'
import { SOURCE_LABELS } from '../../domain/types'
import { Carte, Champ, Confirmation, Encart, Entete } from '../composants'
import { ChoixCandidat, DerouleNuit, MesuresNuit } from '../DerouleNuit'

/**
 * Consultation, correction et saisie manuelle d'une nuit.
 *
 * La meme page sert la creation (`/nuits/nouvelle`) et l'edition
 * (`/nuits/:date`) : les deux manipulent exactement les memes champs, les
 * separer dupliquerait le formulaire pour rien.
 */
export function PageNuit() {
  const { date } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const edition = Boolean(date)

  const donnees = useLiveQuery(async () => {
    const nuit = date ? await db.nuits.get(date) : undefined
    return { nuit, reglages: await lireReglages() }
  }, [date])

  if (!donnees) {
    return (
      <>
        <Entete titre="Nuit" retour />
        <main className="contenu">
          <p className="doux">Chargement…</p>
        </main>
      </>
    )
  }

  if (edition && !donnees.nuit) {
    return (
      <>
        <Entete titre="Nuit" retour />
        <main className="contenu">
          <Encart ton="alerte">Cette nuit n’existe plus.</Encart>
        </main>
      </>
    )
  }

  if (!donnees.nuit) {
    return <Saisie dateSuggeree={params.get('date') ?? todayKey()} onFini={() => navigate('/nuits')} />
  }

  const nuit = donnees.nuit

  return (
    <>
      <Entete titre={formatJour(nuit.date)} sous={SOURCE_LABELS[nuit.source]} retour />
      <main className="contenu">
        <Carte>
          <MesuresNuit nuit={nuit} objectifMinutes={donnees.reglages.objectifMinutes} />
        </Carte>

        <Carte titre="Déroulé">
          <DerouleNuit
            nuit={nuit}
            onBasculer={(i) => void basculerInterruption(nuit.date, i)}
            onSupprimerInterruption={(i) => void supprimerInterruption(nuit.date, i)}
          />
        </Carte>

        {(nuit.candidatsDebut.length > 1 || nuit.candidatsFin.length > 1) && (
          <Carte titre="Autres signaux captés">
            <div className="pile">
              <ChoixCandidat
                titre="Heure de coucher"
                candidats={nuit.candidatsDebut}
                retenu={nuit.debut}
                onChoisir={(c) => void choisirDebut(nuit.date, c.at, c.type)}
              />
              <ChoixCandidat
                titre="Heure de lever"
                candidats={nuit.candidatsFin}
                retenu={nuit.fin}
                onChoisir={(c) => void choisirFin(nuit.date, c.at, c.type)}
              />
            </div>
          </Carte>
        )}

        <Bornes
          debut={nuit.debut}
          fin={nuit.fin}
          notes={nuit.notes ?? ''}
          onEnregistrer={(debut, fin, notes) =>
            void majNuit(nuit.date, { debut, fin, ...(notes ? { notes } : { notes: '' }) })
          }
        />

        <Suppression
          corrigee={nuit.corrigee}
          onSupprimer={() => {
            void supprimerNuit(nuit.date)
            navigate('/nuits')
          }}
        />
      </main>
    </>
  )
}

// ---------------------------------------------------------------------------

function Bornes({
  debut,
  fin,
  notes,
  onEnregistrer,
}: {
  debut: string
  fin: string
  notes: string
  onEnregistrer: (debut: string, fin: string, notes: string) => void
}) {
  const [d, setD] = useState(toDatetimeLocalValue(debut))
  const [f, setF] = useState(toDatetimeLocalValue(fin))
  const [n, setN] = useState(notes)

  // Les bornes peuvent changer sous nos pieds : choisir un autre candidat
  // reecrit la nuit. Sans cette resynchronisation, le formulaire afficherait
  // encore l'ancienne heure et la reappliquerait au prochain enregistrement.
  useEffect(() => setD(toDatetimeLocalValue(debut)), [debut])
  useEffect(() => setF(toDatetimeLocalValue(fin)), [fin])

  const modifie =
    d !== toDatetimeLocalValue(debut) || f !== toDatetimeLocalValue(fin) || n !== notes

  return (
    <Carte titre="Corriger à la main">
      <div className="pile">
        <Champ libelle="Téléphone posé">
          <input type="datetime-local" value={d} onChange={(e) => setD(e.target.value)} />
        </Champ>
        <Champ libelle="Lever">
          <input type="datetime-local" value={f} onChange={(e) => setF(e.target.value)} />
        </Champ>
        <Champ libelle="Notes" aide="Facultatif.">
          <textarea value={n} onChange={(e) => setN(e.target.value)} />
        </Champ>
        <button
          type="button"
          className="btn btn--principal btn--bloc"
          disabled={!modifie}
          onClick={() =>
            onEnregistrer(fromDatetimeLocalValue(d), fromDatetimeLocalValue(f), n.trim())
          }
        >
          Enregistrer
        </button>
      </div>
    </Carte>
  )
}

function Suppression({ corrigee, onSupprimer }: { corrigee: boolean; onSupprimer: () => void }) {
  const [demande, setDemande] = useState(false)

  return (
    <>
      <button type="button" className="btn btn--danger btn--bloc" onClick={() => setDemande(true)}>
        {corrigee ? 'Annuler mes corrections' : 'Supprimer cette nuit'}
      </button>
      {corrigee && (
        <p className="tres-doux centre">
          Les relevés bruts sont conservés : la nuit sera recalculée automatiquement.
        </p>
      )}
      {demande && (
        <Confirmation
          titre={corrigee ? 'Annuler les corrections ?' : 'Supprimer cette nuit ?'}
          message={
            corrigee
              ? 'La nuit reviendra au découpage déduit des relevés. Les relevés eux-mêmes ne sont pas touchés.'
              : 'La nuit sera recalculée à partir des relevés si ceux-ci le permettent encore.'
          }
          libelleConfirmer={corrigee ? 'Annuler' : 'Supprimer'}
          danger
          onConfirmer={() => {
            setDemande(false)
            onSupprimer()
          }}
          onAnnuler={() => setDemande(false)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

function Saisie({ dateSuggeree, onFini }: { dateSuggeree: string; onFini: () => void }) {
  // Par defaut : couche a 23 h la veille, leve a 7 h le jour indique. C'est la
  // forme d'une nuit ordinaire, ce qui laisse en general deux champs a ajuster
  // plutot que quatre.
  const veille = new Date(dateSuggeree)
  veille.setDate(veille.getDate() - 1)
  const [d, setD] = useState(`${dayKeyOf(veille)}T23:00`)
  const [f, setF] = useState(`${dateSuggeree}T07:00`)
  const [n, setN] = useState('')

  const valide = d !== '' && f !== '' && new Date(f) > new Date(d)

  return (
    <>
      <Entete titre="Ajouter une nuit" retour />
      <main className="contenu">
        <Encart ton="info">
          Une nuit saisie à la main n’est jamais recalculée : elle ne sera pas écrasée par
          une synchronisation ultérieure.
        </Encart>

        <Carte>
          <div className="pile">
            <Champ libelle="Téléphone posé">
              <input type="datetime-local" value={d} onChange={(e) => setD(e.target.value)} />
            </Champ>
            <Champ libelle="Lever">
              <input type="datetime-local" value={f} onChange={(e) => setF(e.target.value)} />
            </Champ>
            <Champ libelle="Notes" aide="Facultatif.">
              <textarea value={n} onChange={(e) => setN(e.target.value)} />
            </Champ>
            <button
              type="button"
              className="btn btn--principal btn--bloc"
              disabled={!valide}
              onClick={() => {
                const debut = fromDatetimeLocalValue(d)
                const fin = fromDatetimeLocalValue(f)
                void creerNuitManuelle(dayKeyOf(fin), debut, fin, n.trim() || undefined).then(onFini)
              }}
            >
              Enregistrer
            </button>
          </div>
        </Carte>
      </main>
    </>
  )
}
